import { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import { useInitialDataImplementation } from "@/hooks/implementation/firstLoad/initialDataImplementation";
import { useBusinessCenterBulkLoader } from "@/hooks/implementation/firstLoad/bulkLoader";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";
import { toast } from "@/services/Utils";

import LoadInventoryToolbar from "./LoadInventoryToolbar";
import BusinessCenterSectorTable from "@/components/BusinessCenter/BusinessCenterSectorTable";

import "./rowsLoadStyle.scss";
import FooterButtons from "../Buttons/FooterButtons";

const key = "initial_data_implementation";

const BusinessCenterTableLoad = (props) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const fileInputRef = useRef(null);
	const { customer } = useSelector((state) => state.auth.user);

	const { data } = useInitialDataImplementation();
	const { mutate, isLoading } = useBusinessCenterBulkLoader();

	const [isLockedDownload, setIsLockedDownload] = useState(false);

	const addRow = () => {
		const newBusinessCenter = [
			...data.businessCenters,
			{
				hash: uuidv4(),
				name: "",
				estateParents: [],
				estates: [],
				area: "",
				specie: "",
				variety: "",
				isNew: true,
				sharedIds: "",
			},
		];
		queryClient.setQueryData([key], { ...data, businessCenters: newBusinessCenter });
	};

	const handleInputChange = (index, field, value) => {
		const newBusinessCenters = data.businessCenters.map((row, i) => {
			if (i === index) {
				if (field === "estateParents") {
					return {
						...row,
						[field]: value,
						estates: [],
					};
				}
				return { ...row, [field]: value };
			}
			return row;
		});

		queryClient.setQueryData([key], { ...data, businessCenters: newBusinessCenters });
	};

	const handleExportToExcel = () => {
		setIsLockedDownload(true);
		const filename = "PlantillaCentrosDeCostos.xlsx";
		const params = {};

		const payload = {
			file: {
				key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
				params: params,
				show: true,
				url: "/client/implementation/excel_business_center",
				name: filename,
				type: "excel",
				inExecution: false,
				inProgress: false,
				inChecked: false,
			},
		};

		new Promise((resolve, reject) => {
			try {
				dispatch({
					type: FETCHING_ADD_DOWNLOAD,
					payload: payload,
				});

				setTimeout(() => {
					resolve();
				}, 2000);
			} catch (error) {
				reject(error);
			}
		})
			.then(() => {
				setIsLockedDownload(false);
			})
			.catch(() => {
				setIsLockedDownload(false);
			});
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const reader = new FileReader();

		reader.onload = (e) => {
			const binaryStr = e.target.result;
			const workbook = XLSX.read(binaryStr, { type: "binary" });
			const firstSheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[firstSheetName];
			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			const newRows = jsonData.map((item) => {
				const hash = item["CODIGO"] || uuidv4();
				const name = item["NOMBRE CENTRO DE COSTO"] || "";

				const estateParentObj = data.estateParents.find((ep) => ep.name === item["CAMPO"]);
				const estateObj = data.estates.find((ep) => ep.display_name === item["BODEGA"]);

				const estateParents = estateParentObj ? [{ value: estateParentObj.id, label: estateParentObj.name }] : [];
				const estates = estateObj ? [{ value: estateObj.id, label: estateObj.name }] : [];

				const area = item["SUPERFICIE"] || "";
				const specie = item["ESPECIE"] || "";
				const variety = item["VARIEDAD"] || "";

				return {
					hash,
					name,
					estateParents: estateParents,
					estates,
					area,
					specie,
					variety,
				};
			});

			queryClient.setQueryData([key], { ...data, businessCenters: newRows });
		};

		reader.readAsBinaryString(file);
		fileInputRef.current.value = "";
	};

	const removeRow = (index, isNew) => {
		if (!isNew) {
			Swal.fire({
				title: "Accion de borrado",
				html: "Esta accion tendra efecto al presionar <strong>GUARDAR</strong> o <strong>SIGUIENTE</strong>, ¿deseas proceder?",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#7fb02f",
				cancelButtonColor: "#d33",
				confirmButtonText: "Sí, eliminarlo!",
				cancelButtonText: "Cancelar",
				didOpen: () => {
					document.body.classList.remove("swal2-height-auto");
				},
			}).then((result) => {
				if (result.isConfirmed) {
					const newBusinessCenter = data.businessCenters.filter((_, i) => i !== index);
					queryClient.setQueryData([key], { ...data, businessCenters: newBusinessCenter });

					Swal.fire({
						title: "Eliminado",
						html: "El centro de costo ha sido eliminado",
						icon: "success",
						confirmButtonColor: "#7fb02f",
						confirmButtonText: "OK",
						didOpen: () => {
							document.body.classList.remove("swal2-height-auto");
						},
					});
				} else {
					toast(addToast, "Accion cancelada por el usuario");
				}
			});
		} else {
			const newBusinessCenter = data.businessCenters.filter((_, i) => i !== index);
			queryClient.setQueryData([key], { ...data, businessCenters: newBusinessCenter });
			toast(addToast, "Centro de costo eliminado");
		}
	};

	const validBusinessCenter = () => {
		let isValidBusinessCenter = true;

		for (let i = 0; i < data?.businessCenters.length; i++) {
			const businessCenter = data?.businessCenters[i];

			if (!businessCenter.name) {
				isValidBusinessCenter = false;
				toast(addToast, "Se requiere que el centro de costo tenga nombre y campos asociados,revisa el elemento " + (i + 1) + ".", "error");
				break;
			}

			if (customer.use_sectors && !businessCenter?.businessCenterParent?.id) {
				isValidBusinessCenter = false;
				toast(addToast, "Se requiere que el sector tenga un centro de costo asociado, revisa el elemento " + (i + 1) + ".", "error");
				break;
			}

			if (!customer.use_sectors && businessCenter.estates.length === 0) {
				isValidBusinessCenter = false;
				toast(addToast, "Se requiere que el centro de costo tenga bodega asociada, revisa el elemento " + (i + 1) + ".", "error");
				break;
			}
		}

		return isValidBusinessCenter;
	};

	const saveBusinessCenter = ({ moveForward = false }) => {
		if (validBusinessCenter()) {
			const queryData = { ...queryClient.getQueryData([key]) };
			queryData.implementationStep = moveForward ? data.implementationStep + 1 : data.implementationStep;

			mutate(queryData, {
				onSuccess: (response) => {
					if (Object.prototype.hasOwnProperty.call(response, "businessCenters")) {
						toast(addToast, response.message, "success");

						queryClient.setQueryData([key], {
							...data,
							businessCenters: response.businessCenters,
							implementationStep: response.implementationStep,
						});

						if (props.formWizardRef.current?.nextTab && moveForward) {
							props.formWizardRef.current.nextTab();
						}
					}
				},
			});
		}
	};

	if (data.businessCenters.length === 0) {
		addRow();
	}

	return (
		<>
			<LoadInventoryToolbar
				handleExportToExcel={handleExportToExcel}
				handleFileUpload={handleFileUpload}
				isLockedDownload={isLockedDownload}
				fileInputRef={fileInputRef}
			/>

			<BusinessCenterSectorTable
				data={data}
				handleInputChange={handleInputChange}
				addRow={addRow}
				removeRow={removeRow}
				lockBtn={isLoading}
				crops={data?.crops || []}
				isImplementation={true}
			/>

			<FooterButtons wizard={props.formWizardRef} currentIndex={props.index} handleSave={saveBusinessCenter} isLoading={isLoading} />
		</>
	);
};

export default BusinessCenterTableLoad;
